import React, { useCallback, useEffect, useState } from 'react';
import { Card, DatePicker, Typography, Collapse, Row, Col, Avatar, Statistic, List, message, Tag, Segmented } from 'antd';
import { DownOutlined, UserOutlined, PlusCircleOutlined, MinusCircleOutlined, DollarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import payrollService from '../../../services/payroll/payroll.service';
import { CompanyPayrollItem, CompanyPayrollResponse, PayrollUserDetails } from '../../../types/payroll.types';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import FooterSection from '../../shared/components/footerSection';
import './timesheetReport.css';
import './payrollReport.css';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { getFileUrl } from '../../../utils/file.utils';

dayjs.locale('tr');
dayjs.extend(localizedFormat);

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * PayrollReport Component
 * @description This component displays the company's monthly payroll report.
 * It fetches payroll data from the new net-salary based API.
 */
const PayrollReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [companyPayroll, setCompanyPayroll] = useState<CompanyPayrollResponse>([]);
  const [statusFilter, setStatusFilter] = useState<'active' | 'passive' | 'resigned' | 'all'>('active');

  const year = selectedMonth.year();
  const month = selectedMonth.month() + 1; // dayjs month is 0-indexed
  const { companyId } = useSelector((state: RootState) => state.auth);

  const filteredCompanyPayroll = useMemo(() => {
    if (statusFilter === 'all') {
      return companyPayroll;
    }
    return companyPayroll.filter(user => user.status === statusFilter);
  }, [companyPayroll, statusFilter]);

  /**
   * @function fetchCompanyPayroll
   * @description Fetches the payroll for all employees for the selected month.
   */
  const fetchCompanyPayroll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await payrollService.getCompanyPayroll(Number(companyId), year, month);
      setCompanyPayroll(data || []);
    } catch (error) {
      message.error('Bordro verileri alınırken bir hata oluştu.');
      setCompanyPayroll([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, year, month]);

  useEffect(() => {
    fetchCompanyPayroll();
  }, [fetchCompanyPayroll]);

  const onChangeMonth = (value: dayjs.Dayjs | null) => {
    if (value) setSelectedMonth(value);
  };

  /**
   * @function formatCurrency
   * @description Formats a number into Turkish Lira currency format.
   * @param value The number to format.
   */
  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value || 0);

  /**
   * @function totals
   * @description Calculates total payroll figures for the company summary.
   */
  const totals = useMemo(() => {
    const totalEmployees = companyPayroll?.length || 0;
    const totalBaseNet = companyPayroll?.reduce((sum, item) => sum + (item.baseNetSalary || 0), 0);
    const totalAdditions = companyPayroll?.reduce((sum, item) => sum + (item.totalAdditions || 0), 0);
    const totalDeductions = companyPayroll?.reduce((sum, item) => sum + (item.totalDeductions || 0), 0);
    const totalFinalPayable = companyPayroll?.reduce((sum, item) => sum + (item.finalPayableAmount || 0), 0);
    const avgFinalPayable = totalEmployees > 0 ? totalFinalPayable / totalEmployees : 0;
    return { totalEmployees, totalBaseNet, totalAdditions, totalDeductions, totalFinalPayable, avgFinalPayable };
  }, [companyPayroll]);

  /**
   * @function renderCalculationBreakdown
   * @description Renders the detailed breakdown of payroll calculations.
   * @param breakdown The calculation breakdown object.
   */
  const renderCalculationBreakdown = (breakdown: CompanyPayrollItem['calculationBreakdown']) => (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <Card title={<><PlusCircleOutlined style={{ color: '#52c41a' }} /> Eklemeler</>} bordered={false} size="small">
          <List itemLayout="horizontal">
            <List.Item>
              <List.Item.Meta
                title="Fazla Mesai Kazancı"
                description={`${breakdown.totalOvertimeHours.toFixed(2)} saat`}
              />
              <Text strong style={{ color: '#389e0d' }}>{`+ ${formatCurrency(breakdown.overtimeAddition)}`}</Text>
            </List.Item>
            <List.Item>
              <List.Item.Meta
                title="Resmi Tatil Çalışması Kazancı"
                description={`${breakdown.totalHolidayWorkHours.toFixed(2)} saat`}
              />
              <Text strong style={{ color: '#389e0d' }}>{`+ ${formatCurrency(breakdown.holidayWorkAddition)}`}</Text>
            </List.Item>
          </List>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title={<><MinusCircleOutlined style={{ color: '#f5222d' }} /> Kesintiler</>} bordered={false} size="small">
          <List itemLayout="horizontal">
            <List.Item>
              <List.Item.Meta
                title="Ücretsiz İzin Kesintisi"
                description={`${breakdown.unpaidLeaveDays} gün`}
              />
              <Text strong style={{ color: '#cf1322' }}>{`- ${formatCurrency(breakdown.unpaidLeaveDeduction)}`}</Text>
            </List.Item>
          </List>
        </Card>
      </Col>
      <Col xs={24}>
        <Card title="Hesaplama Detayları" bordered={false} size="small">
          <Row>
            <Col span={12}>
              <Statistic title={<><DollarOutlined /> Günlük Net Ücret</>} value={formatCurrency(breakdown.dailyNetWage)} precision={2} />
            </Col>
            <Col span={12}>
              <Statistic title={<><ClockCircleOutlined /> Saatlik Net Ücret</>} value={formatCurrency(breakdown.hourlyNetWage)} precision={2} />
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );

  /**
   * @function renderUserPanel
   * @description Renders a collapsible panel for each employee's payroll summary.
   * @param u The company payroll item for a user.
   */
  const renderUserPanel = (u: CompanyPayrollItem) => {
    return (
      <Panel
        header={
          <div className="user-card-header" style={{ width: '100%' }}>
            <Row align="middle" justify="space-between" style={{ width: '100%' }}>
              <Col xs={24} sm={8} md={7}>
                <div className="d-flex align-items-center">
                  <Avatar 
                    size={60} 
                    src={u.profilePicture ? getFileUrl(u.profilePicture) : u.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'}
                    icon={<UserOutlined />} 
                    className="me-3" 
                  />
                  <div>
                    <h4 className="mb-1 text-primary fw-bold">{`${u.firstName} ${u.lastName}`}</h4>
                    <Text type="secondary">Departman: {u.department}</Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={16} md={17}>
                <Row gutter={[8, 8]} justify="end">
                  <Col>
                    <Statistic title="Temel Net Maaş" value={formatCurrency(u.baseNetSalary)} />
                  </Col>
                  <Col>
                    <Statistic title="Toplam Eklemeler" value={formatCurrency(u.totalAdditions)} valueStyle={{ color: '#52c41a' }} />
                  </Col>
                  <Col>
                    <Statistic title="Toplam Kesintiler" value={formatCurrency(u.totalDeductions)} valueStyle={{ color: '#f5222d' }} />
                  </Col>
                  <Col>
                    <Statistic title="Ödenecek Tutar" value={formatCurrency(u.finalPayableAmount)} valueStyle={{ fontWeight: 'bold' }} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        }
        key={u.userId}
        style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}
      >
        {renderCalculationBreakdown(u.calculationBreakdown)}
      </Panel>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Bordro Raporu</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><Link to={all_routes.adminDashboard}>Dashboard</Link></li>
                <li className="breadcrumb-item">Raporlar</li>
                <li className="breadcrumb-item active">Bordro Raporu</li>
              </ul>
            </div>
            <div className="col-auto ms-auto d-flex align-items-center gap-2">
              <Segmented
                options={[
                  { label: 'Aktif', value: 'active' },
                  { label: 'Pasif', value: 'passive' },
                  { label: 'İşten Ayrılmış', value: 'resigned' },
                  { label: 'Tümü', value: 'all' },
                ]}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as 'active' | 'passive' | 'resigned' | 'all')}
              />
              <DatePicker picker="month" value={selectedMonth} onChange={onChangeMonth} format="MMMM YYYY" />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="pr-stats-grid">
          <div className="pr-stats-card"><div className="pr-stats-icon primary"><i className="ti ti-users" /></div><div className="pr-stats-content"><div className="pr-stats-label">Toplam Çalışan</div><div className="pr-stats-value">{totals.totalEmployees}</div></div></div>
          <div className="pr-stats-card"><div className="pr-stats-icon secondary"><i className="ti ti-cash" /></div><div className="pr-stats-content"><div className="pr-stats-label">Toplam Temel Net Maaş</div><div className="pr-stats-value">{formatCurrency(totals.totalBaseNet)}</div></div></div>
          <div className="pr-stats-card"><div className="pr-stats-icon success"><i className="ti ti-trending-up" /></div><div className="pr-stats-content"><div className="pr-stats-label">Toplam Eklemeler</div><div className="pr-stats-value">{formatCurrency(totals.totalAdditions)}</div></div></div>
          <div className="pr-stats-card"><div className="pr-stats-icon danger"><i className="ti ti-trending-down" /></div><div className="pr-stats-content"><div className="pr-stats-label">Toplam Kesintiler</div><div className="pr-stats-value">{formatCurrency(totals.totalDeductions)}</div></div></div>
          <div className="pr-stats-card"><div className="pr-stats-icon info"><i className="ti ti-wallet" /></div><div className="pr-stats-content"><div className="pr-stats-label">Toplam Ödenecek</div><div className="pr-stats-value">{formatCurrency(totals.totalFinalPayable)}</div></div></div>
        </div>

        <Card bordered={false} className="pr-card">
          <Title level={4}>
            Aylık Bordro <Text type="secondary">({selectedMonth.format('MMMM YYYY')})</Text>
          </Title>
          {loading ? (
            <LoadingSpinner centered />
          ) : (
            filteredCompanyPayroll && filteredCompanyPayroll.length > 0 ? (
              <Collapse
                className="user-cards pr-panel"
                expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
                expandIconPosition="end"
                ghost
              >
                {filteredCompanyPayroll.map(u => renderUserPanel(u))}
              </Collapse>
            ) : (
              <div className="text-center py-5">
                <Tag>Bu ay için bordro verisi bulunamadı.</Tag>
              </div>
            )
          )}
        </Card>
      </div>
      <FooterSection />
    </div>
  );
};

export default PayrollReport;


